import { atom, useAtomValue } from "jotai"
import { type ExtractAtomValue, type SetStateAction, useAtom, useSetAtom } from "jotai/index"
import { usePathname } from "next/navigation"
import { useEffect } from "react"

export enum loadingVariants {
  DEFAULT = "default",
  PROJECT_NAVIGATION = "projectNavigation",
}

export type ProjectNavigationMetadata = {
  projectName: string
  organizationName: string
}

export type PageStatus =
  | { type: "idle" }
  | {
      type: "loading"
      variant: loadingVariants.DEFAULT
    }
  | {
      type: "loading"
      variant: loadingVariants.PROJECT_NAVIGATION
      metadata?: ProjectNavigationMetadata
    }
  | { type: "done" }

type TriggerOpts = {
  currentPath: string
  path: string
  variant?: loadingVariants
  metadata?: ProjectNavigationMetadata
}

export const pageStatusAtom = atom<PageStatus>({ type: "idle" })
export const usePageStatusAtom = () => useAtom(pageStatusAtom)
export const usePageStatusValue = () => useAtomValue(pageStatusAtom)
export const useSetPageStatus = () => useSetAtom(pageStatusAtom)

const triggerPageLoadingFactory =
  (set: (u: SetStateAction<PageStatus>) => void) =>
  ({ currentPath, path, variant = loadingVariants.DEFAULT, metadata }: TriggerOpts) => {
    set({ type: "idle" })

    if (currentPath !== path) {
      // Small delay to handle cached pages that load very quickly
      const timer = setTimeout(() => {
        if (variant === loadingVariants.PROJECT_NAVIGATION) {
          set({
            type: "loading",
            variant,
            metadata,
          })
        } else {
          set({
            type: "loading",
            variant,
          })
        }
      }, 30)

      // Cleanup function to prevent memory leaks
      return () => clearTimeout(timer)
    }
  }

export const usePageLoaded = () => {
  const setPageStatus = useSetPageStatus()
  const pathname = usePathname()

  useEffect(() => {
    let mounted = true

    // Immediately set to idle when pathname changes
    if (mounted) {
      setPageStatus({ type: "idle" })
    }

    // Use requestAnimationFrame to ensure DOM is ready
    const frame = requestAnimationFrame(() => {
      if (mounted) {
        // Small delay to handle both cached and non-cached pages
        const timer = setTimeout(() => {
          if (mounted) {
            setPageStatus({ type: "done" })
          }
        }, 50)

        return () => clearTimeout(timer)
      }
    })

    return () => {
      mounted = false
      cancelAnimationFrame(frame)
    }
  }, [pathname, setPageStatus])
}

export const useTriggerPageLoading = () => {
  const setPageStatus = useSetPageStatus()
  return triggerPageLoadingFactory(setPageStatus)
}

export const usePageStatus = (): [
  ExtractAtomValue<typeof pageStatusAtom>,
  (options: TriggerOpts) => void,
] => {
  const [pageStatus, setPageStatus] = usePageStatusAtom()

  const triggerPageLoading = triggerPageLoadingFactory(setPageStatus)

  return [pageStatus, triggerPageLoading]
}
